<template>
  <div v-if="modelValue" class="banco-overlay">
    <div class="banco-contenido">
      <LoadingOverlay :visible="cargando" message="Cargando banco de preguntas…" />

      <div class="banco-cabecera">
        <h4 class="banco-titulo">Banco de Preguntas</h4>
        <p v-if="nombreComponente" class="banco-subtitulo">
          Componente: <strong>{{ nombreComponente }}</strong>
        </p>
      </div>

      <div class="banco-buscador">
        <input
          v-model="busqueda"
          type="text"
          placeholder="Buscar por enunciado…"
          class="banco-input"
        />
      </div>

      <div v-if="!cargando" class="banco-lista">
        <div v-if="preguntasFiltradas.length === 0" class="banco-vacio">
          No hay preguntas disponibles en el banco.
        </div>

        <label
          v-for="p in preguntasFiltradas"
          :key="p.id_pregunta"
          class="banco-item"
          :class="{ 'banco-item--sel': seleccionadas.has(p.id_pregunta!) }"
        >
          <input
            type="checkbox"
            class="banco-check"
            :checked="seleccionadas.has(p.id_pregunta!)"
            @change="toggleSeleccion(p)"
          />
          <div class="banco-item-body">
            <span class="banco-enunciado">{{ p.enunciado || p.pregunta }}</span>
            <span class="banco-tag">{{ etiquetaTipo(p.tipo_respuesta || p.tipo || '') }}</span>
            <ul v-if="p.opciones?.length" class="banco-opciones">
              <li v-for="(op, i) in p.opciones" :key="i">{{ op }}</li>
            </ul>
            <div v-if="p.grupo_interes?.nombre" class="banco-grupo">
              Grupo: {{ p.grupo_interes.nombre }}
            </div>
          </div>
        </label>
      </div>

      <div class="banco-footer">
        <span class="banco-count">{{ seleccionadas.size }} seleccionada(s)</span>
        <div class="botones">
          <button class="btn salir" @click="$emit('update:modelValue', false)">Cancelar</button>
          <button
            v-if="authStore.tienePermiso('banco.gestionar')"
            class="btn guardar"
            :disabled="seleccionadas.size === 0"
            @click="confirmar"
          >
            Agregar seleccionadas
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Sugerencias de preguntas desde el banco y desde la IA.
 *
 * La sugerencia automática es opcional y degrada en silencio: si el proveedor no
 * responde, la lista llega vacía y la pregunta se escribe a mano.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch } from 'vue'
import { useGestionBancoPreguntas } from '@/store/GestionBancoPreguntas'
import useAutenticar from '@/store/IngresoAutenticar'
import type Pregunta from '@/interfaces/Preguntas'
import LoadingOverlay from '@/components/LoadingOverlay.vue'

const props = defineProps<{
  modelValue: boolean
  idTipoComponente?: number
  nombreComponente?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  agregar: [preguntas: Pregunta[]]
}>()

const bancoStore = useGestionBancoPreguntas()
const authStore = useAutenticar()
const cargando = ref(false)
const busqueda = ref('')
const seleccionadas = ref(new Set<number>())

watch(() => props.modelValue, async (abierto) => {
  if (abierto) {
    busqueda.value = ''
    seleccionadas.value = new Set()
    cargando.value = true
    await bancoStore.cargarBanco()
    cargando.value = false
  }
})

const preguntasFiltradas = computed(() => {
  const q = busqueda.value.toLowerCase().trim()
  if (!q) return bancoStore.preguntasBanco
  return bancoStore.preguntasBanco.filter(p =>
    (p.enunciado || p.pregunta || '').toLowerCase().includes(q)
  )
})

function toggleSeleccion(p: Pregunta) {
  if (p.id_pregunta == null) return
  const next = new Set(seleccionadas.value)
  if (next.has(p.id_pregunta)) {
    next.delete(p.id_pregunta)
  } else {
    next.add(p.id_pregunta)
  }
  seleccionadas.value = next
}

function etiquetaTipo(tipo: string): string {
  const map: Record<string, string> = {
    single_choice: 'Opción única',
    multiple_choice: 'Opción múltiple',
    opcion_multiple: 'Opción múltiple',
    opinion: 'Opinión',
    long_text: 'Respuesta larga',
    texto: 'Texto',
    escala_likert: 'Escala Likert',
    rating: 'Rating',
    numerica: 'Numérica',
  }
  return map[tipo] || tipo
}

function confirmar() {
  const ids = Array.from(seleccionadas.value)
  const elegidas = bancoStore.preguntasBanco.filter(
    p => p.id_pregunta != null && ids.includes(p.id_pregunta)
  )
  emit('agregar', elegidas)
  emit('update:modelValue', false)
}
</script>

<style scoped>
.banco-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.banco-contenido {
  background: #fff;
  border-radius: 14px;
  width: min(640px, 95vw);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  position: relative;
}

.banco-cabecera {
  background: #0e69a0;
  color: #fff;
  padding: 18px 24px 14px;
}

.banco-titulo {
  margin: 0 0 4px;
  font-size: 1.05rem;
  font-weight: 700;
}

.banco-subtitulo {
  margin: 0;
  font-size: 0.85rem;
  color: #cfe0ec;
}

.banco-buscador {
  padding: 12px 24px;
  border-bottom: 1px solid #d9e1ea;
}

.banco-input {
  width: 100%;
  padding: 0.6rem 0.7rem;
  border: 1px solid #d9e1ea;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  background: #fff;
  color: #1e2a35;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.banco-input:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.15);
}

.banco-lista {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.banco-vacio {
  padding: 32px 24px;
  text-align: center;
  color: #5b6b7a;
  font-size: 0.9rem;
  font-style: italic;
}

.banco-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 24px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #d9e1ea;
}

.banco-item:hover {
  background: #f0f7ff;
}

.banco-item--sel {
  background: #e8f1f8;
}

.banco-check {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: #0e69a0;
}

.banco-item-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.banco-enunciado {
  font-size: 0.9rem;
  line-height: 1.4;
  color: #1e2a35;
}

.banco-tag {
  display: inline-block;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e8f1f8;
  color: #0c447c;
  font-weight: 600;
  align-self: flex-start;
}

.banco-opciones {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 0.8rem;
  color: #5b6b7a;
}

.banco-opciones li {
  margin-bottom: 2px;
}

.banco-grupo {
  font-size: 0.78rem;
  color: #5b6b7a;
}

.banco-footer {
  padding: 14px 24px;
  border-top: 1px solid #d9e1ea;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.banco-count {
  font-size: 0.85rem;
  color: #5b6b7a;
}

.botones {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 0.6rem 1.3rem;
  border-radius: 8px;
  font-size: 0.92rem;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.15s ease;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:hover:not(:disabled) {
  background: #0e69a0;
  color: #fff;
}

.btn.guardar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover {
  background: #d41a1a;
  color: #fff;
  border-color: #d41a1a;
}
</style>
